"use client";

import { useMemo, useState, useEffect, useRef } from "react";
import { format, addMinutes, parse, isSameDay } from "date-fns";
import { TiArrowSortedDown } from "react-icons/ti";
import { LuClock3 } from "react-icons/lu";

import { cn } from "~/utils/fontsConfig";
import { Button } from "~/components/v3/themed/Button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/shadcn/Popover";

interface TimePickerProps {
    value?: string;
    onChange?: (time: string) => void;
    className?: string;
    contentClassName?: string;
    dateToCompare?: Date;
    selectedDate?: Date;
    theme?: "dark" | "light";
}

export function TimePicker({
    value,
    onChange,
    dateToCompare,
    selectedDate,
    className,
    contentClassName,
    theme = "dark",
}: TimePickerProps) {
    const [selectedTime, setSelectedTime] = useState<string>(
        value || "Pick a time",
    );
    const selectedItemRef = useRef<HTMLButtonElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [showIcon, setShowIcon] = useState(false);

    useEffect(() => {
        setSelectedTime(value || "Pick a time");
    }, [value]);

    useEffect(() => {
        const checkWidth = () => {
            if (containerRef.current) {
                const width = containerRef.current?.clientWidth || 0;
                setShowIcon(width > 160);
            }
        };

        checkWidth();
        window.addEventListener("resize", checkWidth);
        return () => window.removeEventListener("resize", checkWidth);
    }, []);

    const timeSlots = useMemo(() => {
        const slots = [];
        const baseDate = selectedDate || new Date();

        // If dateToCompare date matches selectedDate, use dateToCompare time. Otherwise start from 00:00
        const startTime =
            dateToCompare && isSameDay(baseDate, dateToCompare)
                ? parse(format(dateToCompare, "HH:mm"), "HH:mm", baseDate)
                : parse("00:00", "HH:mm", baseDate);

        const endTime = parse("23:30", "HH:mm", baseDate);

        let currentTime = startTime;
        while (currentTime <= endTime) {
            slots.push(format(currentTime, "HH:mm"));
            currentTime = addMinutes(currentTime, 15);
        }
        return slots;
    }, [dateToCompare, selectedDate]);

    const formatTimeWithAMPM = (time: string) => {
        const [hours, minutes] = time.split(":");
        const date = new Date();

        if (!hours || !minutes) return time;

        date.setHours(parseInt(hours), parseInt(minutes));
        return format(date, "hh:mm a");
    };

    const handleTimeSelect = (time: string) => {
        setSelectedTime(time);
        onChange?.(time);
        containerRef.current?.click();
    };

    return (
        <Popover>
            <PopoverTrigger asChild>
                <div
                    ref={containerRef}
                    className={cn(
                        "flex w-full cursor-pointer items-center justify-between rounded-[0.625rem] bg-v3-N-500 p-2 pl-4 pr-2 text-left font-normal",
                        "text-muted-foreground",
                        className,
                        theme === "light" && "bg-white",
                    )}
                >
                    {showIcon && <LuClock3 className="h-4 w-4" />}
                    <span
                        className={cn(
                            "text-v3-N-300",
                            theme === "light" && "text-black",
                        )}
                    >
                        {formatTimeWithAMPM(selectedTime)}
                    </span>
                    <TiArrowSortedDown className="h-4 w-4" />
                </div>
            </PopoverTrigger>
            <PopoverContent
                className={cn(
                    "w-[var(--radix-popover-trigger-width)] border-none p-2",
                    contentClassName,
                    theme === "light" && "bg-white",
                )}
                align="center"
                side="bottom"
                onOpenAutoFocus={(e) => {
                    selectedItemRef.current?.scrollIntoView({
                        block: "center",
                    });
                }}
            >
                <div className="flex max-h-[300px] w-full flex-col items-center overflow-auto rounded-md scrollbar-hide">
                    {timeSlots.map((time) => (
                        <Button
                            key={time}
                            variant="ghost"
                            type="button"
                            ref={
                                selectedTime === time
                                    ? selectedItemRef
                                    : undefined
                            }
                            className={cn(
                                "justify-center rounded-[0.625rem] text-base font-normal shadow-none",
                                "hover:bg-v3-N-400",
                                selectedTime === time &&
                                    "bg-v3-clover text-white hover:bg-v3-clover/80",
                                theme === "light" &&
                                    "hover:bg-black hover:text-white",
                                theme === "light" &&
                                    selectedTime === time &&
                                    "bg-black text-white hover:bg-black/80",
                            )}
                            onClick={() => handleTimeSelect(time)}
                        >
                            {formatTimeWithAMPM(time)}
                        </Button>
                    ))}
                </div>
            </PopoverContent>
        </Popover>
    );
}
